import React from 'react';
import { Server, Shield, Cloud, HardDrive, Network, Wrench } from 'lucide-react';
import { motion } from 'framer-motion';

export const ServicesTI = () => {
  const services = [
    {
      icon: <Server className="w-8 h-8" />,
      title: "Infrastructure TI",
      description: "Conception, installation et maintenance de réseaux d'entreprise performants et sécurisés.",
      features: ["Serveurs & Postes", "Câblage réseau", "Configuration avancée"]
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Cybersécurité",
      description: "Protection complète contre les menaces avec pare-feu, antivirus et surveillance 24/7.",
      features: ["Pare-feu avancés", "Protection ransomware", "Audit sécurité"]
    },
    {
      icon: <Cloud className="w-8 h-8" />,
      title: "Sauvegarde & Cloud",
      description: "Solutions de sauvegarde automatisées et migration cloud sécurisée pour vos données.",
      features: ["Backup automatique", "Cloud hybride", "Récupération rapide"]
    },
    {
      icon: <HardDrive className="w-8 h-8" />,
      title: "Virtualisation",
      description: "Optimisez vos ressources avec des serveurs virtuels performants et évolutifs.",
      features: ["VMware & Hyper-V", "Migration serveurs", "Optimisation ressources"]
    },
    {
      icon: <Network className="w-8 h-8" />,
      title: "Réseaux & WiFi",
      description: "Installation de réseaux sans fil professionnels pour une connectivité optimale.",
      features: ["WiFi entreprise", "VPN sécurisés", "Réseau invité"]
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: "Support Technique",
      description: "Assistance 24/7 avec intervention rapide pour tous vos besoins informatiques.",
      features: ["Support 24/7", "Tickets en ligne", "Intervention rapide"]
    }
  ];

  return (
    <section id="services" className="py-24 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 network-lines opacity-5"></div>
      <div className="absolute top-1/4 left-0 w-96 h-96 bg-[#FF6B35] opacity-5 blur-[150px] rounded-full"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B35] mb-4 block">
            Nos Services
          </span>
          <h2 className="text-4xl sm:text-5xl font-black mb-6 tracking-tight">
            Solutions TI<span className="text-[#FF6B35]"> Complètes</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Des services informatiques professionnels pour optimiser, sécuriser et faire évoluer votre infrastructure technologique
          </p>
        </motion.div>

        {/* Services Bento Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group bg-[#121212] border border-white/5 rounded-xl p-8 hover:border-[#FF6B35]/50 transition-all duration-300 hover:-translate-y-1 h-full flex flex-col"
            >
              {/* Icon */}
              <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center mb-6 text-[#FF6B35] group-hover:bg-[#FF6B35]/20 transition-colors group-hover:shadow-[0_0_20px_rgba(255,107,53,0.3)]">
                {service.icon}
              </div>

              {/* Content */}
              <h3 className="text-2xl font-bold mb-3 group-hover:text-[#FF6B35] transition-colors">
                {service.title}
              </h3>
              <p className="text-gray-400 mb-6 leading-relaxed flex-grow">
                {service.description}
              </p>

              {/* Features */}
              <ul className="space-y-2">
                {service.features.map((feature, i) => (
                  <li key={i} className="flex items-center text-sm text-gray-500">
                    <div className="w-1.5 h-1.5 bg-[#FF6B35] rounded-full mr-2"></div>
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* CTA Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 bg-gradient-to-r from-[#FF6B35] to-[#FF8555] rounded-2xl p-8 md:p-12 text-center"
        >
          <h3 className="text-3xl font-bold mb-4">Besoin d'une solution personnalisée?</h3>
          <p className="text-lg mb-6 opacity-90">Notre équipe d'experts analyse vos besoins et vous propose une solution sur mesure</p>
          <button 
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-black text-white font-semibold px-8 py-4 rounded-lg hover:bg-gray-900 transition-all hover:shadow-[0_0_30px_rgba(0,0,0,0.5)]"
          >
            Parler à un expert
          </button>
        </motion.div>
      </div>
    </section>
  );
};